// DebugLogger.js - Enhanced logging for debugging webview and browser issues

export class DebugLogger {
    static DEBUG_MODE = localStorage.getItem('DEBUG_MODE') === 'true' ||
                        new URLSearchParams(window.location.search).has('debug');

    static logs = [];
    static maxLogs = 1000;

    static log(category, message, data = null) {
        if (!this.DEBUG_MODE) return;

        const timestamp = new Date().toISOString();
        const logEntry = {
            timestamp,
            category,
            message,
            data,
            userAgent: navigator.userAgent,
            url: window.location.href
        };

        // Store in memory
        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }

        // Console output
        const prefix = `[${timestamp.slice(11, 23)}] [${category}]`;
        if (data) {
            console.log(prefix, message, data);
        } else {
            console.log(prefix, message);
        }

        // Also store in localStorage for persistence
        try {
            const storedLogs = JSON.parse(localStorage.getItem('debug_logs') || '[]');
            storedLogs.push(logEntry);
            if (storedLogs.length > 100) {
                storedLogs.shift();
            }
            localStorage.setItem('debug_logs', JSON.stringify(storedLogs));
        } catch (e) {
            // Ignore localStorage errors
        }
    }

    static error(category, message, error) {
        const errorData = {
            message: error?.message,
            stack: error?.stack,
            name: error?.name
        };

        this.log(category, `ERROR: ${message}`, errorData);

        // Always log errors to console, even in non-debug mode
        console.error(`[${category}] ${message}`, error);
    }

    static getDeviceInfo() {
        return {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            vendor: navigator.vendor,
            language: navigator.language,
            screenResolution: `${screen.width}x${screen.height}`,
            windowSize: `${window.innerWidth}x${window.innerHeight}`,
            devicePixelRatio: window.devicePixelRatio,
            touchSupport: 'ontouchstart' in window,
            webGL: this.checkWebGLSupport(),
            webGPU: !!navigator.gpu,
            mediaDevices: !!navigator.mediaDevices,
            localStorage: this.checkLocalStorageSupport(),
            cookies: navigator.cookieEnabled,
            online: navigator.onLine,
            memory: navigator.deviceMemory || 'unknown',
            cores: navigator.hardwareConcurrency || 'unknown'
        };
    }

    static checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
                return {
                    supported: true,
                    version: gl.getParameter(gl.VERSION),
                    vendor: gl.getParameter(gl.VENDOR),
                    renderer: gl.getParameter(gl.RENDERER)
                };
            }
        } catch (e) {
            // Ignore
        }
        return { supported: false };
    }

    static checkLocalStorageSupport() {
        try {
            const test = '__test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    static exportLogs() {
        const exportData = {
            deviceInfo: this.getDeviceInfo(),
            logs: this.logs,
            timestamp: new Date().toISOString()
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `debug_logs_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }

    static showDebugPanel() {
        if (document.getElementById('debug-panel')) return;

        const panel = document.createElement('div');
        panel.id = 'debug-panel';
        panel.style.cssText = `
            position: fixed;
            bottom: 10px;
            right: 10px;
            width: 400px;
            max-height: 300px;
            background: rgba(0, 0, 0, 0.9);
            color: #0f0;
            font-family: monospace;
            font-size: 12px;
            padding: 10px;
            border: 1px solid #0f0;
            border-radius: 5px;
            overflow-y: auto;
            z-index: 100000;
        `;

        const deviceInfo = this.getDeviceInfo();

        panel.innerHTML = `
            <div style="margin-bottom: 10px; border-bottom: 1px solid #0f0; padding-bottom: 5px;">
                <strong>Debug Info</strong>
                <button onclick="DebugLogger.exportLogs()" style="float: right; background: #0f0; color: #000; border: none; padding: 2px 5px; cursor: pointer;">Export</button>
                <button onclick="document.getElementById('debug-panel').remove()" style="float: right; margin-right: 5px; background: #f00; color: #fff; border: none; padding: 2px 5px; cursor: pointer;">X</button>
            </div>
            <div style="font-size: 10px;">
                <div>Platform: ${deviceInfo.platform}</div>
                <div>WebGL: ${deviceInfo.webGL.supported ? 'Yes' : 'No'}</div>
                <div>WebGPU: ${deviceInfo.webGPU ? 'Yes' : 'No'}</div>
                <div>Screen: ${deviceInfo.screenResolution}</div>
                <div>Window: ${deviceInfo.windowSize}</div>
                <div>DPR: ${deviceInfo.devicePixelRatio}</div>
                <div>Memory: ${deviceInfo.memory} GB</div>
                <div>Cores: ${deviceInfo.cores}</div>
            </div>
            <div id="debug-logs" style="margin-top: 10px; max-height: 150px; overflow-y: auto;">
                ${this.logs.slice(-10).map(log =>
                    `<div style="margin: 2px 0; ${log.message.includes('ERROR') ? 'color: #f00;' : ''}">
                        [${log.timestamp.slice(11, 23)}] ${log.category}: ${log.message}
                    </div>`
                ).join('')}
            </div>
        `;

        document.body.appendChild(panel);

        // Make DebugLogger globally available for the export button
        window.DebugLogger = DebugLogger;
    }

    static init() {
        if (this.DEBUG_MODE) {
            console.log('%c🔧 Debug Mode Enabled', 'color: #0f0; font-size: 16px; font-weight: bold;');
            console.log('Device Info:', this.getDeviceInfo());

            // Show debug panel after a delay
            setTimeout(() => this.showDebugPanel(), 1000);

            // Log unhandled errors
            window.addEventListener('error', (e) => {
                this.error('Window', 'Unhandled error', e.error || e);
            });

            window.addEventListener('unhandledrejection', (e) => {
                this.error('Window', 'Unhandled promise rejection', e.reason);
            });
        }
    }
}

// Auto-initialize if debug mode is enabled
if (DebugLogger.DEBUG_MODE) {
    DebugLogger.init();
}

export default DebugLogger;
